import { useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { attendanceService } from "@/services/attendanceService";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useAttendancePerformance } from "@/hooks/useAttendance";
import { cn } from "@/lib/utils";
import {
  IncidentPerformanceScore,
  PerformanceRanking,
  WorkstationEvaluationHistoryItem,
  WorkstationEvaluationHistoryResponse,
  WorkstationStationBreakdown,
} from "@/types/attendance";
import {
  Activity,
  AlertTriangle,
  Award,
  BarChart3,
  CalendarRange,
  CheckSquare,
  ClipboardCheck,
  Download,
  Eye,
  FileSpreadsheet,
  Loader2,
  Medal,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  User,
} from "lucide-react";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const today = new Date().toISOString().split("T")[0];

const getChartColors = (primary: string, secondary: string) => [
  primary,
  secondary,
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
];

const formatDate = (value: string | null) => {
  if (!value) return "Sin registro";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-SV", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const sanitizeSheetName = (value: string) =>
  value.replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Hoja";

const getScoreColor = (score: number) => {
  if (score >= 95) return "text-emerald-600";
  if (score >= 85) return "text-blue-600";
  if (score >= 70) return "text-amber-600";
  if (score > 0) return "text-rose-600";
  return "text-slate-400";
};

const getScoreBarColor = (score: number) => {
  if (score >= 95) return "from-emerald-500 to-teal-500";
  if (score >= 85) return "from-blue-500 to-cyan-500";
  if (score >= 70) return "from-amber-500 to-orange-500";
  if (score > 0) return "from-rose-500 to-pink-500";
  return "from-slate-300 to-slate-400";
};

const getQualityBadgeClass = (qualityLevel: string) => {
  switch (qualityLevel) {
    case "Excelente":
      return "bg-emerald-50 text-emerald-700";
    case "Sobresaliente":
      return "bg-blue-50 text-blue-700";
    case "Estable":
      return "bg-amber-50 text-amber-700";
    case "En Riesgo":
      return "bg-rose-50 text-rose-700";
    default:
      return "bg-slate-100 text-slate-500";
  }
};

const getOperationalBadgeClass = (level: string) => {
  switch (level) {
    case "Muy Alto":
      return "bg-emerald-50 text-emerald-700";
    case "Alto":
      return "bg-blue-50 text-blue-700";
    case "Estable":
      return "bg-amber-50 text-amber-700";
    case "Bajo":
      return "bg-rose-50 text-rose-700";
    default:
      return "bg-slate-100 text-slate-500";
  }
};

const downloadWorkbook = (workbook: XLSX.WorkBook, fileName: string) => {
  saveAs(
    new Blob([
      XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      }),
    ]),
    `${fileName}.xlsx`,
  );
};

type DetailPayload = {
  employee: PerformanceRanking;
  history: WorkstationEvaluationHistoryResponse | null;
  incidentScore: IncidentPerformanceScore | null;
};

export const PerformanceDashboard = () => {
  const { user, company, can } = useAuth();
  const primaryColor = company?.primary_color || "#2563eb";
  const secondaryColor = company?.secondary_color || "#64748b";
  const chartColors = useMemo(() => getChartColors(primaryColor, secondaryColor), [primaryColor, secondaryColor]);
  const slug = company?.slug || "";
  const canManageEvaluations = can("attendance.setup");

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedStationFilter, setSelectedStationFilter] = useState("all");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isBulkExporting, setIsBulkExporting] = useState(false);

  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [workstationName, setWorkstationName] = useState("");
  const [score, setScore] = useState("95");
  const [maxScore, setMaxScore] = useState("100");
  const [evaluationDate, setEvaluationDate] = useState(today);
  const [notes, setNotes] = useState("");

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskName, setTaskName] = useState("");
  const [taskDate, setTaskDate] = useState(today);

  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState(false);
  const [incidentDate, setIncidentDate] = useState(today);
  const [incidentNotes, setIncidentNotes] = useState("");

  const {
    performanceRanking,
    isLoadingPerformance,
    evaluationHistory,
    isLoadingEvaluationHistory,
    incidentPerformanceScore,
    isLoadingIncidentPerformanceScore,
    createWorkstationEvaluation,
    createQualityIncident,
    createProductionTask,
  } = useAttendancePerformance({
    slug,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    employee_id: isDetailModalOpen ? selectedEmployeeId || undefined : undefined,
  });

  const filteredRanking = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return performanceRanking || [];

    return (performanceRanking || []).filter((item: PerformanceRanking) =>
      [
        item.name,
        item.identification,
        item.position,
        item.area,
        item.lastWorkstationName || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [performanceRanking, searchTerm]);

  const selectedEmployee = useMemo(
    () =>
      (performanceRanking || []).find(
        (employee: PerformanceRanking) => employee.id === selectedEmployeeId,
      ) || null,
    [performanceRanking, selectedEmployeeId],
  );

  const evaluatedEmployees = useMemo(
    () =>
      (performanceRanking || []).filter(
        (item: PerformanceRanking) => item.totalEvaluations > 0,
      ),
    [performanceRanking],
  );

  const topEmployee = useMemo(() => {
    if (evaluatedEmployees.length === 0) return null;
    return evaluatedEmployees.reduce((best, current) =>
      current.score > best.score ? current : best,
    );
  }, [evaluatedEmployees]);

  const teamAverage = useMemo(() => {
    if (evaluatedEmployees.length === 0) return 0;
    return (
      Math.round(
        (evaluatedEmployees.reduce(
          (acc, current) => acc + current.averageScore,
          0,
        ) /
          evaluatedEmployees.length) *
          100,
      ) / 100
    );
  }, [evaluatedEmployees]);

  const totalEvaluations = useMemo(
    () =>
      (performanceRanking || []).reduce(
        (acc: number, current: PerformanceRanking) =>
          acc + current.totalEvaluations,
        0,
      ),
    [performanceRanking],
  );

  const totalStations = useMemo(
    () =>
      (performanceRanking || []).reduce(
        (acc: number, current: PerformanceRanking) =>
          acc + current.coveredStations,
        0,
      ),
    [performanceRanking],
  );

  useEffect(() => {
    const visibleIds = new Set((performanceRanking || []).map((item) => item.id));
    setSelectedEmployeeIds((current) =>
      current.filter((employeeIdItem) => visibleIds.has(employeeIdItem)),
    );

    if (selectedEmployeeId && !visibleIds.has(selectedEmployeeId)) {
      setSelectedEmployeeId("");
      setIsDetailModalOpen(false);
    }
  }, [performanceRanking, selectedEmployeeId]);

  useEffect(() => {
    if (!isDetailModalOpen) {
      setSelectedStationFilter("all");
    }
  }, [isDetailModalOpen]);

  useEffect(() => {
    if (!isEvaluationModalOpen) {
      setEmployeeId(selectedEmployeeId || "");
    }
  }, [isEvaluationModalOpen, selectedEmployeeId]);

  const stationOptions = useMemo(
    () =>
      (evaluationHistory?.stationBreakdown || []).map(
        (station) => station.workstationName,
      ),
    [evaluationHistory],
  );

  const filteredStationBreakdown = useMemo(() => {
    const stations = evaluationHistory?.stationBreakdown || [];
    if (selectedStationFilter === "all") return stations;
    return stations.filter(
      (station) => station.workstationName === selectedStationFilter,
    );
  }, [evaluationHistory, selectedStationFilter]);

  const filteredEvaluations = useMemo(() => {
    const evaluations = evaluationHistory?.evaluations || [];
    if (selectedStationFilter === "all") return evaluations;
    return evaluations.filter(
      (evaluation) => evaluation.workstationName === selectedStationFilter,
    );
  }, [evaluationHistory, selectedStationFilter]);

  const stationAverageChartData = useMemo(
    () =>
      filteredStationBreakdown.map((station) => ({
        station: station.workstationName,
        averageScore: station.averageScore,
        latestScore: station.latestScore,
      })),
    [filteredStationBreakdown],
  );

  const stationTrendData = useMemo(() => {
    if (!filteredEvaluations.length) return [];

    const grouped = filteredEvaluations
      .slice()
      .sort(
        (a, b) =>
          new Date(a.evaluationDate).getTime() -
          new Date(b.evaluationDate).getTime(),
      )
      .reduce<Record<string, Record<string, string | number>>>((acc, item) => {
        const key = item.evaluationDate;
        if (!acc[key]) {
          acc[key] = {
            date: key,
            label: formatDate(key),
          };
        }
        acc[key][item.workstationName] = item.normalizedScore;
        return acc;
      }, {});

    return Object.values(grouped);
  }, [filteredEvaluations]);

  const stationKeys = useMemo(
    () =>
      Array.from(
        new Set(filteredEvaluations.map((evaluation) => evaluation.workstationName)),
      ),
    [filteredEvaluations],
  );

  const isAllFilteredSelected =
    filteredRanking.length > 0 &&
    filteredRanking.every((employee) => selectedEmployeeIds.includes(employee.id));

  const selectedEmployeesForExport = useMemo(
    () =>
      (performanceRanking || []).filter((employee) =>
        selectedEmployeeIds.includes(employee.id),
      ),
    [performanceRanking, selectedEmployeeIds],
  );

  const toggleEmployeeSelection = (employeeIdValue: string) => {
    setSelectedEmployeeIds((current) =>
      current.includes(employeeIdValue)
        ? current.filter((id) => id !== employeeIdValue)
        : [...current, employeeIdValue],
    );
  };

  const toggleAllFiltered = () => {
    if (isAllFilteredSelected) {
      const filteredIds = new Set(filteredRanking.map((employee) => employee.id));
      setSelectedEmployeeIds((current) =>
        current.filter((id) => !filteredIds.has(id)),
      );
      return;
    }

    setSelectedEmployeeIds((current) =>
      Array.from(
        new Set([...current, ...filteredRanking.map((employee) => employee.id)]),
      ),
    );
  };

  const openEmployeeDetail = (employeeIdValue: string) => {
    setSelectedEmployeeId(employeeIdValue);
    setIsDetailModalOpen(true);
  };

  const buildWorkbookForEmployee = (
    payload: DetailPayload,
    workbook: XLSX.WorkBook,
    options?: { consolidated?: boolean },
  ) => {
    const { employee, history, incidentScore } = payload;
    if (!history || !incidentScore) return;

    const summaryRows = [
      {
        Empleado: employee.name,
        Identificacion: employee.identification,
        Area: employee.area,
        Cargo: employee.position,
        Fecha_Inicio: startDate || "Sin filtro",
        Fecha_Fin: endDate || "Sin filtro",
        Score_Calidad: employee.score,
        Nivel_Calidad: employee.qualityLevel,
        Evaluaciones: history.summary.totalEvaluations,
        Estaciones: history.summary.coveredStations,
        Mejor_Estacion: history.summary.bestStationName || "N/D",
        Estacion_Atencion: history.summary.worstStationName || "N/D",
        Score_Operativo: incidentScore.score,
        Nivel_Operativo: incidentScore.performanceLevel,
        Tareas_OK: incidentScore.completedTasks,
        Incidentes_Alta: incidentScore.highIncidents,
        Tasa_Incidentes: incidentScore.incidentRate,
      },
    ];

    const stationRows = history.stationBreakdown.map((station) => ({
      Empleado: employee.name,
      Estacion: station.workstationName,
      Evaluaciones: station.evaluationsCount,
      Score_Promedio: station.averageScore,
      Ultimo_Score: station.latestScore,
      Ultima_Evaluacion: station.lastEvaluationDate || "N/D",
    }));

    const historyRows = history.evaluations.map((evaluation) => ({
      Empleado: employee.name,
      Fecha: evaluation.evaluationDate,
      Estacion: evaluation.workstationName,
      Score_Obtenido: evaluation.score,
      Score_Maximo: evaluation.maxScore,
      Score_Normalizado: evaluation.normalizedScore,
      Evaluador: evaluation.evaluatorName || "N/D",
      Notas: evaluation.notes || "",
    }));

    if (options?.consolidated) {
      return { summaryRows, stationRows, historyRows };
    }

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summaryRows),
      sanitizeSheetName(`Resumen_${employee.name}`),
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(stationRows),
      sanitizeSheetName(`Estaciones_${employee.name}`),
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(historyRows),
      sanitizeSheetName(`Historico_${employee.name}`),
    );

    return null;
  };

  const loadEmployeeDetailPayload = async (
    employee: PerformanceRanking,
  ): Promise<DetailPayload> => {
    const [historyResponse, incidentScoreResponse] = await Promise.all([
      attendanceService.getWorkstationEvaluationHistory(
        slug,
        employee.id,
        startDate || undefined,
        endDate || undefined,
      ),
      attendanceService.getIncidentPerformanceScore(
        slug,
        employee.id,
        startDate || undefined,
        endDate || undefined,
      ),
    ]);

    return {
      employee,
      history: historyResponse.data?.data || null,
      incidentScore: incidentScoreResponse.data?.data || null,
    };
  };

  const handleExportExcel = () => {
    if (!selectedEmployee || !evaluationHistory || !incidentPerformanceScore) return;

    const workbook = XLSX.utils.book_new();
    buildWorkbookForEmployee(
      {
        employee: selectedEmployee,
        history: evaluationHistory,
        incidentScore: incidentPerformanceScore,
      },
      workbook,
    );

    downloadWorkbook(
      workbook,
      `detalle_desempeno_${selectedEmployee.name.replace(/\s+/g, "_").toLowerCase()}`,
    );
  };

  const handleBulkExportExcel = async () => {
    if (selectedEmployeesForExport.length === 0) {
      toast.error("Selecciona al menos un empleado para exportar.");
      return;
    }

    try {
      setIsBulkExporting(true);
      const payloads = await Promise.all(
        selectedEmployeesForExport.map((employee) =>
          loadEmployeeDetailPayload(employee),
        ),
      );

      const workbook = XLSX.utils.book_new();
      const summaryRows: Record<string, unknown>[] = [];
      const stationRows: Record<string, unknown>[] = [];
      const historyRows: Record<string, unknown>[] = [];

      payloads.forEach((payload) => {
        const rows = buildWorkbookForEmployee(payload, workbook, {
          consolidated: true,
        });
        if (!rows) return;
        summaryRows.push(...rows.summaryRows);
        stationRows.push(...rows.stationRows);
        historyRows.push(...rows.historyRows);
      });

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(summaryRows),
        "Resumen General",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(stationRows),
        "Estaciones",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(historyRows),
        "Historico",
      );

      downloadWorkbook(
        workbook,
        `desempeno_masivo_${today}`,
      );
      toast.success("Exportación masiva generada");
    } catch (error) {
      console.error("Error exporting bulk performance workbook:", error);
      toast.error("No se pudo generar la exportación masiva");
    } finally {
      setIsBulkExporting(false);
    }
  };

  const handleExportPdf = () => {
    if (!selectedEmployee || !evaluationHistory || !incidentPerformanceScore) return;

    const stationRows = filteredStationBreakdown
      .map(
        (station) => `
          <tr>
            <td>${station.workstationName}</td>
            <td>${station.evaluationsCount}</td>
            <td>${station.averageScore}%</td>
            <td>${station.latestScore}%</td>
            <td>${formatDate(station.lastEvaluationDate)}</td>
          </tr>
        `,
      )
      .join("");

    const historyRows = filteredEvaluations
      .map(
        (evaluation) => `
          <tr>
            <td>${formatDate(evaluation.evaluationDate)}</td>
            <td>${evaluation.workstationName}</td>
            <td>${evaluation.normalizedScore}%</td>
            <td>${evaluation.evaluatorName || "N/D"}</td>
            <td>${evaluation.notes || "Sin observaciones"}</td>
          </tr>
        `,
      )
      .join("");

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Detalle de Desempeno - ${selectedEmployee.name}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #0f172a; padding: 24px; }
            h1, h2, h3 { margin: 0 0 12px; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 20px 0; }
            .card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; background: #f8fafc; }
            .muted { color: #64748b; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; font-size: 12px; }
            th { background: #eff6ff; }
            .section { margin-top: 28px; }
          </style>
        </head>
        <body>
          <h1>Detalle de Desempeno por Empleado</h1>
          <p class="muted">
            Empleado: ${selectedEmployee.name} | Periodo: ${startDate || "Sin filtro"} al ${endDate || "Sin filtro"} | Estacion: ${selectedStationFilter === "all" ? "Todas" : selectedStationFilter}
          </p>
          <div class="grid">
            <div class="card"><div class="muted">Score de Calidad</div><h2>${selectedEmployee.score}%</h2><div>${selectedEmployee.qualityLevel}</div></div>
            <div class="card"><div class="muted">Score Operativo</div><h2>${incidentPerformanceScore.score}</h2><div>${incidentPerformanceScore.performanceLevel}</div></div>
            <div class="card"><div class="muted">Tareas Completadas</div><h2>${incidentPerformanceScore.completedTasks}</h2></div>
            <div class="card"><div class="muted">Incidentes Alta</div><h2>${incidentPerformanceScore.highIncidents}</h2></div>
          </div>
          <div class="section">
            <h3>Desglose por Estacion</h3>
            <table>
              <thead><tr><th>Estacion</th><th>Evaluaciones</th><th>Score Promedio</th><th>Ultimo Score</th><th>Ultima Evaluacion</th></tr></thead>
              <tbody>${stationRows}</tbody>
            </table>
          </div>
          <div class="section">
            <h3>Historico de Evaluaciones</h3>
            <table>
              <thead><tr><th>Fecha</th><th>Estacion</th><th>Score</th><th>Evaluador</th><th>Notas</th></tr></thead>
              <tbody>${historyRows}</tbody>
            </table>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 350);
  };

  const handleCreateEvaluation = async () => {
    if (!employeeId || !workstationName.trim() || !score || !evaluationDate || !company?.id) {
      return;
    }

    await createWorkstationEvaluation.mutateAsync({
      employee_id: employeeId,
      company_id: company.id,
      slug,
      workstation_name: workstationName.trim(),
      score: Number(score),
      max_score: Number(maxScore || 100),
      evaluation_date: evaluationDate,
      evaluator_name:
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        user?.email ||
        "",
      notes: notes.trim(),
    });

    setEmployeeId(selectedEmployeeId || "");
    setWorkstationName("");
    setScore("95");
    setMaxScore("100");
    setEvaluationDate(today);
    setNotes("");
    setIsEvaluationModalOpen(false);
  };

  const handleCreateTask = async () => {
    if (!selectedEmployeeId || !taskName.trim() || !taskDate || !company?.id) return;

    await createProductionTask.mutateAsync({
      employee_id: selectedEmployeeId,
      company_id: company.id,
      slug,
      name: taskName.trim(),
      status: "Completed",
      date: taskDate,
    });

    setTaskName("");
    setTaskDate(today);
    setIsTaskModalOpen(false);
  };

  const handleCreateIncident = async () => {
    if (!selectedEmployeeId || !incidentDate || !company?.id) return;

    await createQualityIncident.mutateAsync({
      employee_id: selectedEmployeeId,
      company_id: company.id,
      slug,
      severity: "High",
      date: incidentDate,
      description: incidentNotes.trim(),
    });

    setIncidentDate(today);
    setIncidentNotes("");
    setIsIncidentModalOpen(false);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
  };

  if (isLoadingPerformance) {
    return (
      <div className="p-8 text-center text-slate-500">
        Cargando ranking de calidad por empleado...
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <style>{`
        :root {
          --perf-primary: ${primaryColor};
          --perf-secondary: ${secondaryColor};
        }
        .perf-card-primary {
          background-color: ${primaryColor}15;
          border-color: ${primaryColor}30;
        }
        .perf-card-secondary {
          background-color: ${secondaryColor}15;
          border-color: ${secondaryColor}30;
        }
        .perf-text-primary { color: var(--perf-primary); }
        .perf-text-secondary { color: var(--perf-secondary); }
        .perf-bg-primary { background-color: var(--perf-primary); }
        .perf-bg-secondary { background-color: var(--perf-secondary); }
      `}</style>
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl border perf-card-primary">
                <Medal className="h-6 w-6 perf-text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Mejor Calidad
                </p>
                <p className="text-lg font-bold text-slate-900 truncate">
                  {topEmployee?.name || "Sin datos"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {topEmployee ? `${topEmployee.score}% promedio` : "Aún no hay evaluaciones"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl border perf-card-secondary">
                <ShieldCheck className="h-6 w-6 perf-text-secondary" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Calidad Promedio
                </p>
                <p className="text-2xl font-bold text-slate-900">{teamAverage}%</p>
                <p className="text-xs text-slate-500 mt-1">Promedio global del equipo evaluado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-violet-50 border border-violet-100">
                <ClipboardCheck className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Evaluaciones
                </p>
                <p className="text-2xl font-bold text-slate-900">{totalEvaluations}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {evaluatedEmployees.length} empleados con historial
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-amber-50 border border-amber-100">
                <BarChart3 className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Estaciones Cubiertas
                </p>
                <p className="text-2xl font-bold text-slate-900">{totalStations}</p>
                <p className="text-xs text-slate-500 mt-1">Suma de estaciones evaluadas por persona</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200/70 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-slate-100 bg-white">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-slate-900 text-white">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-slate-900">
                    Ranking de Calidad por Empleado
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Haz clic en ver detalle para abrir toda la información del colaborador en un pop up.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
                <div className="relative w-full xl:w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar colaborador, área o estación..."
                    className="pl-9 bg-slate-50 border-slate-200"
                  />
                </div>

                <div className="flex gap-2">
                  <div className="relative">
                    <CalendarRange className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(event) => setStartDate(event.target.value)}
                      className="pl-9 bg-slate-50 border-slate-200"
                    />
                  </div>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="bg-slate-50 border-slate-200"
                  />
                  <Button variant="outline" onClick={resetFilters} className="border-slate-200">
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Limpiar
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckSquare className="h-4 w-4" />
                {selectedEmployeeIds.length} colaboradores seleccionados para exportación masiva
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={handleBulkExportExcel}
                  disabled={selectedEmployeeIds.length === 0 || isBulkExporting}
                >
                  {isBulkExporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                  )}
                  Exportar seleccionados
                </Button>

                {canManageEvaluations && (
                  <Dialog open={isEvaluationModalOpen} onOpenChange={setIsEvaluationModalOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                        <Plus className="mr-2 h-4 w-4" />
                        Registrar evaluación
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[520px]">
                      <DialogHeader>
                        <DialogTitle>Nueva evaluación de estación</DialogTitle>
                        <DialogDescription>
                          Registra el resultado de calidad para alimentar el ranking de KPIs.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid gap-4 py-2">
                        <div className="grid gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Colaborador
                          </label>
                          <Select value={employeeId} onValueChange={setEmployeeId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona un empleado" />
                            </SelectTrigger>
                            <SelectContent>
                              {(performanceRanking || []).map((employee: PerformanceRanking) => (
                                <SelectItem key={employee.id} value={employee.id}>
                                  {employee.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Estación de trabajo
                          </label>
                          <Input
                            value={workstationName}
                            onChange={(event) => setWorkstationName(event.target.value)}
                            placeholder="Ej: Ensamble final - Línea 2"
                          />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-3">
                          <div className="grid gap-2">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Puntaje obtenido
                            </label>
                            <Input type="number" min="0" step="0.01" value={score} onChange={(event) => setScore(event.target.value)} />
                          </div>

                          <div className="grid gap-2">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Puntaje máximo
                            </label>
                            <Input type="number" min="1" step="0.01" value={maxScore} onChange={(event) => setMaxScore(event.target.value)} />
                          </div>

                          <div className="grid gap-2">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Fecha
                            </label>
                            <Input type="date" value={evaluationDate} onChange={(event) => setEvaluationDate(event.target.value)} />
                          </div>
                        </div>

                        <div className="grid gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Observaciones
                          </label>
                          <Textarea
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                            placeholder="Comentarios sobre hallazgos, defectos o cumplimiento del estándar..."
                          />
                        </div>
                      </div>

                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsEvaluationModalOpen(false)}>
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleCreateEvaluation}
                          disabled={createWorkstationEvaluation.isPending}
                          className="h-10 px-6 rounded-lg perf-bg-primary hover:opacity-90 text-white transition-all shadow-sm"
                        >
                          {createWorkstationEvaluation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="mr-2 h-4 w-4" />
                          )}
                          Guardar evaluación
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {filteredRanking.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                <ClipboardCheck className="h-6 w-6 text-slate-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Sin resultados para mostrar</h3>
              <p className="mt-2 text-sm text-slate-500">
                Ajusta el filtro o registra la primera evaluación de estación para comenzar a medir calidad.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                  <TableHead className="w-12 text-center">
                    <Checkbox checked={isAllFilteredSelected} onCheckedChange={toggleAllFiltered} />
                  </TableHead>
                  <TableHead className="w-16 text-center">Rank</TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead className="text-center">Evaluaciones</TableHead>
                  <TableHead className="text-center">Estaciones</TableHead>
                  <TableHead className="text-center">Tendencia</TableHead>
                  <TableHead>Última evaluación</TableHead>
                  <TableHead className="w-36 text-center">Detalle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRanking.map((item: PerformanceRanking, index: number) => {
                  const progressWidth = Math.max(item.score, item.totalEvaluations > 0 ? 6 : 0);

                  return (
                    <TableRow key={item.id} className="hover:bg-slate-50/60">
                      <TableCell className="text-center" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selectedEmployeeIds.includes(item.id)}
                          onCheckedChange={() => toggleEmployeeSelection(item.id)}
                        />
                      </TableCell>

                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          {index === 0 ? (
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-yellow-100">
                              <Medal className="h-4 w-4 text-yellow-700" />
                            </div>
                          ) : (
                            <span className="text-sm font-bold text-slate-400">#{index + 1}</span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 border border-slate-200">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500">
                              {item.area} • {item.position}
                            </p>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="min-w-[180px]">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-bold ${getScoreColor(item.score)}`}>
                              {item.score}%
                            </span>
                            <Badge className={getQualityBadgeClass(item.qualityLevel)}>
                              {item.qualityLevel}
                            </Badge>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            {/* eslint-disable-next-line */}
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${getScoreBarColor(item.score)}`}
                              style={{ width: `${progressWidth}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                          {item.totalEvaluations}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                          {item.coveredStations}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-center">
                        {item.totalEvaluations === 0 ? (
                          <span className="text-xs text-slate-400">Sin historial</span>
                        ) : item.scoreTrend >= 0 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                            <TrendingUp className="h-4 w-4" />
                            {item.scoreTrend} pts
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
                            <TrendingDown className="h-4 w-4" />
                            {Math.abs(item.scoreTrend)} pts
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-slate-800">
                            {item.lastWorkstationName || "Pendiente"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDate(item.lastEvaluationDate)}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          className="border-slate-200"
                          onClick={() => openEmployeeDetail(item.id)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalle
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[95vw] xl:max-w-[1500px] max-h-[92vh] overflow-y-auto p-0">
          {selectedEmployee && (
            <>
              <DialogHeader className="border-b border-slate-100 bg-white px-6 py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <DialogTitle className="text-xl text-slate-900">
                      Detalle de {selectedEmployee.name}
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                      Todo el histórico del colaborador en un pop up, con filtro por estación y exportación directa.
                    </DialogDescription>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Select value={selectedStationFilter} onValueChange={setSelectedStationFilter}>
                      <SelectTrigger className="w-[220px] bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Filtrar por estación" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las estaciones</SelectItem>
                        {stationOptions.map((station) => (
                          <SelectItem key={station} value={station}>
                            {station}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      className="border-slate-200"
                      onClick={handleExportExcel}
                      disabled={!evaluationHistory || !incidentPerformanceScore}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Excel
                    </Button>

                    <Button
                      variant="outline"
                      className="border-slate-200"
                      onClick={handleExportPdf}
                      disabled={!evaluationHistory || !incidentPerformanceScore}
                    >
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      PDF
                    </Button>

                    {canManageEvaluations && (
                      <>
                        <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="border-slate-200">
                              <Activity className="mr-2 h-4 w-4" />
                              Tarea OK
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[420px]">
                            <DialogHeader>
                              <DialogTitle>Registrar tarea completada</DialogTitle>
                              <DialogDescription>
                                Esto alimenta la métrica operativa del colaborador seleccionado.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-2">
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Empleado
                                </label>
                                <Input value={selectedEmployee.name} disabled />
                              </div>
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Nombre de la tarea
                                </label>
                                <Input
                                  value={taskName}
                                  onChange={(event) => setTaskName(event.target.value)}
                                  placeholder="Ej: Lote inspeccionado y liberado"
                                />
                              </div>
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Fecha
                                </label>
                                <Input type="date" value={taskDate} onChange={(event) => setTaskDate(event.target.value)} />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="ghost" onClick={() => setIsTaskModalOpen(false)}>
                                Cancelar
                              </Button>
                              <Button
                                onClick={handleCreateTask}
                                disabled={createProductionTask.isPending}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                {createProductionTask.isPending ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="mr-2 h-4 w-4" />
                                )}
                                Guardar tarea
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        <Dialog open={isIncidentModalOpen} onOpenChange={setIsIncidentModalOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="border-slate-200">
                              <AlertTriangle className="mr-2 h-4 w-4" />
                              Incidente alto
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[420px]">
                            <DialogHeader>
                              <DialogTitle>Registrar incidente de severidad alta</DialogTitle>
                              <DialogDescription>
                                Este registro impacta el score operativo de incidentes vs tareas completadas.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-2">
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Empleado
                                </label>
                                <Input value={selectedEmployee.name} disabled />
                              </div>
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Fecha
                                </label>
                                <Input type="date" value={incidentDate} onChange={(event) => setIncidentDate(event.target.value)} />
                              </div>
                              <div className="grid gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Descripción
                                </label>
                                <Textarea
                                  value={incidentNotes}
                                  onChange={(event) => setIncidentNotes(event.target.value)}
                                  placeholder="Describe el hallazgo crítico o la no conformidad detectada..."
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="ghost" onClick={() => setIsIncidentModalOpen(false)}>
                                Cancelar
                              </Button>
                              <Button
                                onClick={handleCreateIncident}
                                disabled={createQualityIncident.isPending}
                                className="bg-rose-600 hover:bg-rose-700 text-white"
                              >
                                {createQualityIncident.isPending ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="mr-2 h-4 w-4" />
                                )}
                                Guardar incidente
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 p-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Score de Calidad
                    </p>
                    <p className={`mt-2 text-3xl font-bold ${getScoreColor(selectedEmployee.score)}`}>
                      {selectedEmployee.score}%
                    </p>
                    <Badge className={`mt-3 ${getQualityBadgeClass(selectedEmployee.qualityLevel)}`}>
                      {selectedEmployee.qualityLevel}
                    </Badge>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Histórico Evaluado
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900">
                      {filteredEvaluations.length}
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                      {filteredStationBreakdown.length} estaciones visibles
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Score Operativo
                    </p>
                    {isLoadingIncidentPerformanceScore ? (
                      <div className="mt-4 flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Cargando...
                      </div>
                    ) : (
                      <>
                        <p className="mt-2 text-3xl font-bold text-slate-900">
                          {incidentPerformanceScore?.score ?? 0}
                        </p>
                        <Badge className={`mt-3 ${getOperationalBadgeClass(incidentPerformanceScore?.performanceLevel || "")}`}>
                          {incidentPerformanceScore?.performanceLevel || "Sin Actividad"}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600" />
                      <p className="font-semibold text-slate-900">Desglose por estación</p>
                    </div>
                    <div className="mt-4 space-y-3">
                      {isLoadingEvaluationHistory ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cargando histórico...
                        </div>
                      ) : filteredStationBreakdown.length ? (
                        filteredStationBreakdown.map((station: WorkstationStationBreakdown) => (
                          <div key={station.workstationName} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="font-medium text-slate-900">{station.workstationName}</p>
                                <p className="text-xs text-slate-500">{station.evaluationsCount} evaluaciones</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${getScoreColor(station.averageScore)}`}>
                                  {station.averageScore}%
                                </p>
                                <p className="text-xs text-slate-500">
                                  Última: {formatDate(station.lastEvaluationDate)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">
                          No hay evaluaciones para la estación seleccionada en este periodo.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-blue-600" />
                      <p className="font-semibold text-slate-900">Métrica operativa BE</p>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      Esta métrica sigue separada del ranking de calidad y conserva la fórmula de incidentes alta vs tareas completadas.
                    </p>

                    {isLoadingIncidentPerformanceScore ? (
                      <div className="mt-4 flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Calculando score operativo...
                      </div>
                    ) : (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <div className="rounded-xl bg-emerald-50 p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
                            Tareas OK
                          </p>
                          <p className="mt-2 text-2xl font-bold text-emerald-700">
                            {incidentPerformanceScore?.completedTasks ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl bg-rose-50 p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-rose-600">
                            Incidentes Alta
                          </p>
                          <p className="mt-2 text-2xl font-bold text-rose-700">
                            {incidentPerformanceScore?.highIncidents ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl bg-slate-100 p-4 sm:col-span-2">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                Fórmula de score
                              </p>
                              <p className="mt-2 font-mono text-sm text-slate-700">
                                {incidentPerformanceScore?.scoreFormula || "(completedTasks / (highIncidents + 1)) * 100"}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-3xl font-bold text-slate-900">
                                {incidentPerformanceScore?.score ?? 0}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                Tasa de incidentes: {incidentPerformanceScore?.incidentRate ?? 0}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <p className="font-semibold text-slate-900">Tendencia por estación</p>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      Evolución temporal del score normalizado de la estación filtrada.
                    </p>
                    <div className="mt-4 h-[280px]">
                      {stationTrendData.length > 0 && stationKeys.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={stationTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" stroke="#64748b" />
                            <YAxis domain={[0, 100]} stroke="#64748b" />
                            <Tooltip />
                            <Legend />
                            {stationKeys.map((stationName, index) => (
                              <Line
                                key={stationName}
                                type="monotone"
                                dataKey={stationName}
                                stroke={chartColors[index % chartColors.length]}
                                strokeWidth={2}
                                connectNulls
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-slate-500">
                          No hay suficientes datos para graficar tendencia.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600" />
                      <p className="font-semibold text-slate-900">
                        Promedio y último score por estación
                      </p>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      Comparativo rápido para detectar estaciones consistentes o en riesgo.
                    </p>
                    <div className="mt-4 h-[280px]">
                      {stationAverageChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsBarChart data={stationAverageChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="station" stroke="#64748b" />
                            <YAxis domain={[0, 100]} stroke="#64748b" />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="averageScore" name="Promedio" fill="#2563eb" radius={[6, 6, 0, 0]} />
                            <Bar dataKey="latestScore" name="Último" fill="#0f766e" radius={[6, 6, 0, 0]} />
                          </RechartsBarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-slate-500">
                          No hay estaciones evaluadas para graficar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-semibold text-slate-900">Histórico de evaluaciones</p>
                      <p className="text-xs text-slate-500">
                        Detalle cronológico de evaluaciones por estación.
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-white text-slate-700">
                      {filteredEvaluations.length} registros
                    </Badge>
                  </div>

                  {isLoadingEvaluationHistory ? (
                    <div className="p-6 text-slate-500 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Cargando histórico...
                    </div>
                  ) : filteredEvaluations.length ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-white hover:bg-white">
                          <TableHead>Fecha</TableHead>
                          <TableHead>Estación</TableHead>
                          <TableHead className="text-center">Score</TableHead>
                          <TableHead>Evaluador</TableHead>
                          <TableHead>Notas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvaluations.map((evaluation: WorkstationEvaluationHistoryItem) => (
                          <TableRow key={evaluation.id}>
                            <TableCell>{formatDate(evaluation.evaluationDate)}</TableCell>
                            <TableCell className="font-medium text-slate-900">
                              {evaluation.workstationName}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant="secondary"
                                className={getQualityBadgeClass(
                                  evaluation.normalizedScore >= 95
                                    ? "Excelente"
                                    : evaluation.normalizedScore >= 85
                                      ? "Sobresaliente"
                                      : evaluation.normalizedScore >= 70
                                        ? "Estable"
                                        : "En Riesgo",
                                )}
                              >
                                {evaluation.normalizedScore}%
                              </Badge>
                            </TableCell>
                            <TableCell>{evaluation.evaluatorName || "N/D"}</TableCell>
                            <TableCell className="text-slate-500">
                              {evaluation.notes || "Sin observaciones"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="p-6 text-sm text-slate-500">
                      Este colaborador no tiene evaluaciones para la estación y periodo seleccionados.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
